import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, MailCheck } from 'lucide-react';
import { authApi } from '@features/auth/api/auth.api';
import {
  MIN_PASSWORD_LENGTH,
  newPasswordSchema,
  requestResetSchema,
  verifyCodeSchema,
  type NewPasswordForm,
  type RequestResetForm,
  type VerifyCodeForm,
} from '@features/auth/model/auth.types';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { PasswordInput } from '@shared/ui/PasswordInput';
import { Alert } from '@shared/ui/Alert';
import { paths } from '@app/router/paths';
import { toApiError } from '@shared/api/api-error';
import { env } from '@shared/config/env';

type Step = 'email' | 'code' | 'password' | 'done';

/**
 * Three-step reset: ask for the address, check the emailed code, set a new
 * password.
 *
 * The email and code are held here rather than in the URL — a reset code in a
 * link is a credential in browser history and in any referrer header.
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="w-full max-w-sm">
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-semibold text-slate-900">{env.appName}</h1>
        <p className="mt-1 text-sm text-slate-500">
          {step === 'done' ? 'Password updated' : 'Reset your password'}
        </p>
      </div>

      {error && (
        <Alert tone="error" className="mb-4">
          {error}
        </Alert>
      )}

      {step === 'email' && (
        <EmailStep
          onSent={(value) => {
            setEmail(value);
            setError(null);
            setStep('code');
          }}
          onError={setError}
        />
      )}

      {step === 'code' && (
        <CodeStep
          email={email}
          onVerified={(value) => {
            setCode(value);
            setError(null);
            setStep('password');
          }}
          onError={setError}
          onBack={() => {
            setError(null);
            setStep('email');
          }}
        />
      )}

      {step === 'password' && (
        <PasswordStep
          email={email}
          code={code}
          onDone={() => {
            setError(null);
            setStep('done');
          }}
          onError={setError}
        />
      )}

      {step === 'done' && (
        <div className="space-y-4 text-center">
          <CheckCircle2 aria-hidden className="mx-auto h-10 w-10 text-periscope-600" />
          <p className="text-sm text-slate-600">
            Your password has been changed. You can sign in with it now.
          </p>
          <Button size="lg" className="w-full" onClick={() => navigate(paths.login)}>
            Go to sign in
          </Button>
        </div>
      )}

      {step !== 'done' && (
        <p className="mt-6 text-center">
          <Link
            to={paths.login}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
            Back to sign in
          </Link>
        </p>
      )}
    </div>
  );
}

function EmailStep({
  onSent,
  onError,
}: {
  onSent: (email: string) => void;
  onError: (message: string | null) => void;
}) {
  const [isSending, setSending] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestResetForm>({
    resolver: zodResolver(requestResetSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async ({ email }) => {
    setSending(true);
    onError(null);

    try {
      await authApi.requestPasswordReset(email);
      // The server does not say whether the address exists, so neither does the
      // UI: it always advances to the code step.
      onSent(email);
    } catch (caught) {
      onError(toApiError(caught).message);
    } finally {
      setSending(false);
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <p className="text-sm text-slate-500">
        Enter your admin email and we’ll send you a 6-digit code.
      </p>

      <Input
        label="Email"
        type="email"
        autoComplete="username"
        autoFocus
        placeholder="you@mbrellafilms.com"
        error={errors.email?.message}
        {...register('email')}
      />

      <Button type="submit" size="lg" isLoading={isSending} className="w-full">
        Send code
      </Button>
    </form>
  );
}

function CodeStep({
  email,
  onVerified,
  onError,
  onBack,
}: {
  email: string;
  onVerified: (code: string) => void;
  onError: (message: string | null) => void;
  onBack: () => void;
}) {
  const [isChecking, setChecking] = useState(false);
  const [isResending, setResending] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<VerifyCodeForm>({
    resolver: zodResolver(verifyCodeSchema),
    defaultValues: { code: '' },
  });

  const onSubmit = handleSubmit(async ({ code }) => {
    setChecking(true);
    onError(null);

    try {
      await authApi.verifyResetCode(email, code);
      onVerified(code);
    } catch (caught) {
      onError(toApiError(caught).message);
    } finally {
      setChecking(false);
    }
  });

  const resend = async () => {
    setResending(true);
    onError(null);

    try {
      await authApi.requestPasswordReset(email);
    } catch (caught) {
      onError(toApiError(caught).message);
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <div className="flex items-start gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600">
        <MailCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span>
          If <span className="font-medium text-slate-900">{email}</span> is an admin account, a code
          is on its way. It expires in 15 minutes.
        </span>
      </div>

      <Input
        label="6-digit code"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        placeholder="123456"
        className="text-center text-lg tracking-[0.4em]"
        error={errors.code?.message}
        {...register('code')}
      />

      <Button type="submit" size="lg" isLoading={isChecking} className="w-full">
        Continue
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onBack}
          className="text-slate-500 transition-colors hover:text-slate-900"
        >
          Use a different email
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={isResending}
          className="text-brand-600 transition-colors hover:underline disabled:opacity-60"
        >
          {isResending ? 'Sending…' : 'Resend code'}
        </button>
      </div>
    </form>
  );
}

function PasswordStep({
  email,
  code,
  onDone,
  onError,
}: {
  email: string;
  code: string;
  onDone: () => void;
  onError: (message: string | null) => void;
}) {
  const [isSaving, setSaving] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewPasswordForm>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async ({ password }) => {
    setSaving(true);
    onError(null);

    try {
      await authApi.resetPassword(email, code, password);
      onDone();
    } catch (caught) {
      onError(toApiError(caught).message);
    } finally {
      setSaving(false);
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <PasswordInput
        label="New password"
        autoComplete="new-password"
        autoFocus
        hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
        error={errors.password?.message}
        {...register('password')}
      />

      <PasswordInput
        label="Confirm new password"
        autoComplete="new-password"
        error={errors.confirmPassword?.message}
        {...register('confirmPassword')}
      />

      <Button type="submit" size="lg" isLoading={isSaving} className="w-full">
        Change password
      </Button>
    </form>
  );
}
